import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import useFetch from '../../../../hooks/useFetch';
import MasterLookupService from '../../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../../enums';

interface CurrencyOption {
  id: number;
  currency_code: string;
  currency_name: string;
}

interface CountryFormData {
  country_code: string;
  country_name: string;
  region: string;
  default_currency_id: number | '';
  status: 'active' | 'inactive';
  notes: string;
}

const initialForm: CountryFormData = {
  country_code: '',
  country_name: '',
  region: '',
  default_currency_id: '',
  status: 'active',
  notes: '',
};

const CountryForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<CountryFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [activeCurrencies, setActiveCurrencies] = useState<CurrencyOption[]>([]);

  useFetch({
    service: MasterLookupService,
    func: 'list',
    payload: { type: 'currencies', includeDeleted: false, limit: 100 },
    dependency: [],
    onSuccess: (data: any) => setActiveCurrencies(data?.items ?? []),
  });

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterLookupService.get('countries', id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          country_code: row.country_code ?? '',
          country_name: row.country_name ?? '',
          region: row.region ?? '',
          default_currency_id: row.default_currency_id ?? '',
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: CountryFormData) => {
      const nextErrors: Record<string, string> = {};
      if (!data.country_code.trim()) nextErrors.country_code = t('masters.countries.form.pageTexts.fields.countryCode.errors.required');
      if (!data.country_name.trim()) nextErrors.country_name = t('masters.countries.form.pageTexts.fields.countryName.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value =
      name === 'status'
        ? e.target.checked
          ? 'active'
          : 'inactive'
        : name === 'default_currency_id'
          ? e.target.value === ''
            ? ''
            : Number(e.target.value)
          : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = {
          ...formData,
          default_currency_id: formData.default_currency_id === '' ? undefined : formData.default_currency_id,
        };

        if (edit && id) {
          await MasterLookupService.update('countries', id, payload);
          toast.success(<Toast message={t('masters.countries.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterLookupService.create('countries', payload);
          toast.success(<Toast message={t('masters.countries.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.countries}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.countries.form.pageTexts.editTitle') : t('masters.countries.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-3">
          <Input
            id="country_code"
            name="country_code"
            label={t('masters.countries.form.pageTexts.fields.countryCode.label')}
            placeholder={t('masters.countries.form.pageTexts.fields.countryCode.placeholder')}
            required
            value={formData.country_code}
            error={!!errors.country_code}
            errorMessage={errors.country_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="country_name"
            name="country_name"
            label={t('masters.countries.form.pageTexts.fields.countryName.label')}
            placeholder={t('masters.countries.form.pageTexts.fields.countryName.placeholder')}
            required
            value={formData.country_name}
            error={!!errors.country_name}
            errorMessage={errors.country_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <Input
            id="region"
            name="region"
            label={t('masters.countries.form.pageTexts.fields.region.label')}
            placeholder={t('masters.countries.form.pageTexts.fields.region.placeholder')}
            value={formData.region}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <CustomizedSwitch
            label={t('masters.countries.form.pageTexts.fields.status.label')}
            name="status"
            checked={formData.status === 'active'}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="default_currency_id">
            {t('masters.countries.form.pageTexts.fields.defaultCurrency.label')}
          </label>
          <select
            id="default_currency_id"
            name="default_currency_id"
            className="form-select"
            value={formData.default_currency_id}
            onChange={(e) => handleChange(e, 'default_currency_id')}
          >
            <option value="">{t('masters.countries.form.pageTexts.fields.defaultCurrency.placeholder')}</option>
            {activeCurrencies.map((currency) => (
              <option key={currency.id} value={currency.id}>
                {currency.currency_code} — {currency.currency_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.countries.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.countries.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.countries.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.countries}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.countries.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default CountryForm;
